import { useState, useEffect, useCallback } from 'react';
import { estimateAPI } from '../../services/api';

/**
 * DutyRates — CRUD table for managing duty rate configurations.
 * Fetches all rates on mount and provides inline editing per row.
 */
const DutyRates = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await estimateAPI.getDutyRates();
      setRates(result.data || []);
    } catch (err) {
      setError('Failed to load duty rates');
      console.error('DutyRates fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const handleEdit = (rate) => {
    setEditingId(rate.id);
    setEditForm({
      country: rate.country || '',
      hsCode: rate.hsCode || '',
      originCountry: rate.originCountry || '',
      ratePercent: rate.ratePercent ?? '',
      notes: rate.notes || '',
      effectiveFrom: rate.effectiveFrom ? rate.effectiveFrom.split('T')[0] : '',
      effectiveUntil: rate.effectiveUntil ? rate.effectiveUntil.split('T')[0] : ''
    });
    setFeedback(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setFeedback(null);

      const data = {
        ...editForm,
        ratePercent: editForm.ratePercent !== '' ? Number(editForm.ratePercent) : undefined,
        effectiveFrom: editForm.effectiveFrom || undefined,
        effectiveUntil: editForm.effectiveUntil || undefined
      };

      await estimateAPI.updateDutyRate(editingId, data);
      setFeedback({ type: 'success', message: 'Duty rate updated successfully' });
      setEditingId(null);
      setEditForm({});
      await fetchRates();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to update duty rate' });
      console.error('DutyRates save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) return <div className="loading">Loading duty rates...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Duty Rates</h1>
      </div>

      {feedback && (
        <div className={feedback.type === 'success' ? 'success-banner' : 'error-message'}
          style={{ marginBottom: '1rem' }}>
          <p>{feedback.message}</p>
        </div>
      )}

      {rates.length === 0 ? (
        <div className="empty-state">
          <p>No duty rates configured yet.</p>
        </div>
      ) : (
        <table className="simple-table">
          <thead>
            <tr>
              <th>Country</th>
              <th>HS Code</th>
              <th>Origin Country</th>
              <th>Rate %</th>
              <th>Notes</th>
              <th>Effective From</th>
              <th>Effective Until</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((rate) => (
              <tr key={rate.id}>
                {editingId === rate.id ? (
                  <>
                    <td>
                      <input
                        className="admin-inline-input"
                        type="text"
                        value={editForm.country}
                        onChange={(e) => handleChange('country', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input"
                        type="text"
                        value={editForm.hsCode}
                        onChange={(e) => handleChange('hsCode', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input"
                        type="text"
                        value={editForm.originCountry}
                        onChange={(e) => handleChange('originCountry', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input admin-inline-narrow"
                        type="number"
                        step="0.01"
                        value={editForm.ratePercent}
                        onChange={(e) => handleChange('ratePercent', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input"
                        type="text"
                        value={editForm.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input"
                        type="date"
                        value={editForm.effectiveFrom}
                        onChange={(e) => handleChange('effectiveFrom', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input"
                        type="date"
                        value={editForm.effectiveUntil}
                        onChange={(e) => handleChange('effectiveUntil', e.target.value)}
                      />
                    </td>
                    <td className="actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{rate.country || '-'}</td>
                    <td><code>{rate.hsCode || '-'}</code></td>
                    <td>{rate.originCountry || '-'}</td>
                    <td>{rate.ratePercent != null ? `${Number(rate.ratePercent).toFixed(2)}%` : '-'}</td>
                    <td className="text-small">{rate.notes || '-'}</td>
                    <td>{formatDate(rate.effectiveFrom)}</td>
                    <td>{formatDate(rate.effectiveUntil)}</td>
                    <td className="actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(rate)}
                      >
                        Edit
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DutyRates;
